import { z } from "zod";
import { createEndpoint } from "../createEndpoint";
import type { HttpClient } from "../httpClient";
import {
    BillingCheckoutSummarySchema,
    BillingInvitationOfferPreviewSchema,
    ExpertBillingPortalSessionSchema,
    ExpertPendingCheckoutResponseSchema,
    ExpertSubscriptionOverviewSchema,
    RedeemPlanInvitationRequestSchema,
} from "../schemas/expert-billing";

/**
 * Expert Phase 0 billing surface for invitation redemption and recovery of a
 * still-open hosted Checkout. Both backend responses are private/no-store;
 * callers must keep invitation tokens memory-only.
 */
export function createExpertBillingApi(http: HttpClient, baseUrl: string) {
    const expertBaseUrl = `${baseUrl}/expert`;

    return {
        previewInvitation: createEndpoint<
            z.infer<typeof BillingInvitationOfferPreviewSchema>,
            z.input<typeof RedeemPlanInvitationRequestSchema>
        >(http, "POST", "/billing-invitations/preview", {
            base: expertBaseUrl,
            responseSchema: BillingInvitationOfferPreviewSchema,
        }),

        redeemInvitation: createEndpoint<
            z.infer<typeof BillingCheckoutSummarySchema>,
            z.input<typeof RedeemPlanInvitationRequestSchema>
        >(http, "POST", "/billing-invitations/redeem", {
            base: expertBaseUrl,
            responseSchema: BillingCheckoutSummarySchema,
        }),

        getPendingCheckout: createEndpoint<z.infer<typeof ExpertPendingCheckoutResponseSchema>>(
            http,
            "GET",
            "/subscription",
            {
                base: expertBaseUrl,
                responseSchema: ExpertPendingCheckoutResponseSchema,
            },
        ),

        getSubscriptionOverview: createEndpoint<z.infer<typeof ExpertSubscriptionOverviewSchema>>(
            http,
            "GET",
            "/subscription",
            {
                base: expertBaseUrl,
                responseSchema: ExpertSubscriptionOverviewSchema,
            },
        ),

        createPortalSession: createEndpoint<z.infer<typeof ExpertBillingPortalSessionSchema>>(
            http,
            "POST",
            "/billing/portal-session",
            {
                base: expertBaseUrl,
                responseSchema: ExpertBillingPortalSessionSchema,
            },
        ),
    };
}

export type ExpertBillingApi = ReturnType<typeof createExpertBillingApi>;
